import { useContext, useEffect, useMemo, useState } from 'react';
import { ContentContext } from './context/ContentContext';
import type { Tile } from './context/ContentContext/contentTypes';
import { getPerTileWltpCompliance } from './wltpCompliance';

export interface UseWltpTileFilterArgs {
  isMarketComplianceEnabled?: boolean;
  initialWltpCompliance?: boolean[];
}

export interface UseWltpTileFilterResult {
  filteredTiles: Tile[];
  isGateActive: boolean;
}

export const useWltpTileFilter = ({
  isMarketComplianceEnabled,
  initialWltpCompliance,
}: UseWltpTileFilterArgs): UseWltpTileFilterResult => {
  const context = useContext(ContentContext);
  const content = context.content;
  const { vueFormatterService, localeService, logger } = context;

  const typedContent = content as { tiles?: Tile[] } | undefined;
  const tiles = useMemo<Tile[]>(() => typedContent?.tiles ?? [], [typedContent]);
  const isGateActive = Boolean(isMarketComplianceEnabled);

  const [resolvedCompliance, setResolvedCompliance] = useState<
    { keySig: string; compliance: boolean[] } | undefined
  >(undefined);

  const allKeys = useMemo(
    () => (isGateActive ? tiles.flatMap((tile: Tile) => tile.wltpKeys ?? []) : []),
    [isGateActive, tiles],
  );
  const keySig = useMemo(
    () => JSON.stringify(tiles.map((tile: Tile) => tile.wltpKeys ?? [])),
    [tiles],
  );

  const initialCompliance = useMemo(() => {
    if (!isGateActive) return undefined;
    if (!initialWltpCompliance || initialWltpCompliance.length !== tiles.length) return undefined;
    return initialWltpCompliance;
  }, [isGateActive, initialWltpCompliance, tiles]);

  useEffect(() => {
    if (!isGateActive) return undefined;
    if (!vueFormatterService || !localeService) return undefined;
    if (!allKeys.length) return undefined;

    const controller = new AbortController();
    const runCheck = async () => {
      const compliance = await getPerTileWltpCompliance(
        tiles,
        vueFormatterService,
        localeService,
        logger,
      );
      if (!controller.signal.aborted) {
        setResolvedCompliance({ keySig, compliance });
      }
    };
    void runCheck();
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGateActive, keySig, vueFormatterService, localeService, logger]);

  if (!isGateActive) {
    return { filteredTiles: tiles, isGateActive: false };
  }

  if (!allKeys.length) {
    return { filteredTiles: tiles, isGateActive: true };
  }

  const compliance =
    resolvedCompliance?.keySig === keySig ? resolvedCompliance.compliance : initialCompliance;

  if (!compliance) {
    return { filteredTiles: [], isGateActive: true };
  }

  const filteredTiles = tiles.filter((_, index) => compliance[index]);
  return { filteredTiles, isGateActive: true };
};
